import React, { Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom'
import Header from './Components/Header/Header'
import Footer from './Components/Footer/Footer'
import BottomNav from './Components/BottomNav/BottomNav'
import LoginSignup from './Components/Auth/LoginSignup'
import AdminLayout from './Components/Admin/AdminLayout'
import LogoLoader from './Components/Loader/LogoLoader'
import ScrollToTop from './Components/ScrollToTop/ScrollToTop'
import SiloNavigation from './Components/Common/SiloNavigation'

// Lazy loaded page components
const Home = lazy(() => import('./Pages/Home'))
const Product = lazy(() => import('./Pages/Product'))
const Cakes = lazy(() => import('./Pages/Cakes'))
const Cart = lazy(() => import('./Pages/Cart'))
const ContactUs = lazy(() => import('./Pages/ContactUs'))
const AboutUs = lazy(() => import('./Pages/About_Us'))
const Faq = lazy(() => import('./Pages/Faq'))
const Reviews = lazy(() => import('./Pages/Reviews'))
const Blogs = lazy(() => import('./Pages/Blogs'))
const BlogDetail = lazy(() => import('./Pages/BlogDetail'))
const Personalized = lazy(() => import('./Pages/Personalized'))
const Occasions = lazy(() => import('./Pages/Occasions'))
const Combos = lazy(() => import('./Pages/Combos'))
const RefundPolicy = lazy(() => import('./Pages/Policies/RefundPolicy'))
const PrivacyPolicy = lazy(() => import('./Pages/Policies/PrivacyPolicy'))
const CancellationPolicy = lazy(() => import('./Pages/Policies/CancellationPolicy'))
const TermsConditions = lazy(() => import('./Pages/Policies/TermsConditions'))
const PaymentsSecurity = lazy(() => import('./Pages/Policies/PaymentsSecurity'))
const Wishes = lazy(() => import('./Pages/Wishes'))
const InNews = lazy(() => import('./Pages/InNews'))
const CustomizePage = lazy(() => import('./Pages/CustomizePage'))
const LocationPage = lazy(() => import('./Pages/LocationPage'))
const Sitemap = lazy(() => import('./Pages/Sitemap'))
const OrderSuccess = lazy(() => import('./Pages/OrderSuccess'))
const OrderFailed = lazy(() => import('./Pages/OrderFailed'))
const MyOrders = lazy(() => import('./Pages/MyOrders'))
const NotFound = lazy(() => import('./Pages/NotFound'))

// Birthday Pages
const BirthdayCakes = lazy(() => import('./Pages/Birthday/BirthdayCakes'))
const KidsBirthdayCakes = lazy(() => import('./Pages/Birthday/KidsBirthdayCakes'))
const BirthdayCupcakes = lazy(() => import('./Pages/Birthday/BirthdayCupcakes'))

// Anniversary Pages
const AnniversaryCakes = lazy(() => import('./Pages/Anniversary/AnniversaryCakes'))
const CoupleCakes = lazy(() => import('./Pages/Anniversary/CoupleCakes'))
const AnniversaryCupcakes = lazy(() => import('./Pages/Anniversary/AnniversaryCupcakes'))

// PhotoCakes Pages
const PhotoCakes = lazy(() => import('./Pages/PhotoCakes/PhotoCakes'))
const KidsPhotoCakes = lazy(() => import('./Pages/PhotoCakes/KidsPhotoCakes'))
const PhotoCupcakes = lazy(() => import('./Pages/PhotoCakes/PhotoCupcakes'))

// Flowers Pages
const AllFlowers = lazy(() => import('./Pages/FlowersLink/AllFlowers'))
const ValentineFlowers = lazy(() => import('./Pages/FlowersLink/ValentineFlowers'))
const BirthdayFlowers = lazy(() => import('./Pages/FlowersLink/BirthdayFlowers'))
const AnniversaryFlowers = lazy(() => import('./Pages/FlowersLink/AnniversaryFlowers'))

// CakeTypes Pages
const DesignerCakes = lazy(() => import('./Pages/CakeTypes/DesignerCakes'))
const ThemeCakes = lazy(() => import('./Pages/CakeTypes/ThemeCakes'))
const KidsCakes = lazy(() => import('./Pages/CakeTypes/KidsCakes'))

// CakeFlavours Pages
const ChocolateCakes = lazy(() => import('./Pages/CakeFlavours/ChocolateCakes'))
const ButterscotchCakes = lazy(() => import('./Pages/CakeFlavours/ButterscotchCakes'))
const RedVelvetCakes = lazy(() => import('./Pages/CakeFlavours/RedVelvetCakes'))
const PineappleCakes = lazy(() => import('./Pages/CakeFlavours/PineappleCakes'))
const BlackForestCakes = lazy(() => import('./Pages/CakeFlavours/BlackForestCakes'))

// CakeCombos Pages
const CakeWithFlowers = lazy(() => import('./Pages/CakeCombos/CakeWithFlowers'))
const CakeWithTeddy = lazy(() => import('./Pages/CakeCombos/CakeWithTeddy'))
const CakeWithChocolates = lazy(() => import('./Pages/CakeCombos/CakeWithChocolates'))

// Occasions Pages
const RepublicDayCakes = lazy(() => import('./Pages/Occasions/RepublicDayCakes'))
const ValentineDayCakes = lazy(() => import('./Pages/Occasions/ValentinesCakes'))
const Rakhi = lazy(() => import('./Pages/Occasions/Rakhi'))
const BhaiDoojCakes = lazy(() => import('./Pages/Occasions/BhaiDoojCakes'))
const DussehraCakes = lazy(() => import('./Pages/Occasions/DussehraCakes'))
const DiwaliCakes = lazy(() => import('./Pages/Occasions/DiwaliCakes'))
const HoliCakes = lazy(() => import('./Pages/Occasions/HoliCakes'))
const ChristmasCakes = lazy(() => import('./Pages/Occasions/ChristmasCakes'))

// Relations Pages
const CakesForGirlfriend = lazy(() => import('./Pages/Relations/CakesForGirlfriend'))
const CakesForBoyfriend = lazy(() => import('./Pages/Relations/CakesForBoyfriend'))
const CakesForWife = lazy(() => import('./Pages/Relations/CakesForWife'))
const CakesForHusband = lazy(() => import('./Pages/Relations/CakesForHusband'))
const CakesForSister = lazy(() => import('./Pages/Relations/CakesForSister'))
const CakesForBrother = lazy(() => import('./Pages/Relations/CakesForBrother'))
const CakesForDaughter = lazy(() => import('./Pages/Relations/CakesForDaughter'))
const CakesForSon = lazy(() => import('./Pages/Relations/CakesForSon'))
const CakesForFriend = lazy(() => import('./Pages/Relations/CakesForFriend'))
const CakesForKids = lazy(() => import('./Pages/Relations/CakesForKids'))
const WomensDayCakes = lazy(() => import('./Pages/Relations/WomensDayCakes'))
const CakesForGrandMother = lazy(() => import('./Pages/Relations/CakesForGrandMother'))
const CakesForGrandFather = lazy(() => import('./Pages/Relations/CakesForGrandFather'))
const CakesForMother = lazy(() => import('./Pages/Relations/CakesForMother'))
const CakesForFather = lazy(() => import('./Pages/Relations/CakesForFather'))
const MothersDayCakes = lazy(() => import('./Pages/Relations/MothersDayCakes'))
const FathersDayCakes = lazy(() => import('./Pages/Relations/FathersDayCakes'))

const Approutes = () => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isHomePage = location.pathname === '/';
  const hideNav = isAuthPage || isAdminPage;

  return (
    <div>
      <ScrollToTop />
      {!hideNav && <Header />}
      <main className={`main-content ${isHomePage ? 'home-padding' : isAdminPage || isAuthPage ? '' : 'inner-padding'}`}>

        <Suspense fallback={<LogoLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/product/:productSlug" element={<Product />} />
          <Route path="/occasions" element={<Occasions />} />
          <Route path="/combos" element={<Combos />} />
          <Route path="/birthday-cakes" element={<BirthdayCakes />} />
          <Route path="/kids-birthday-cakes" element={<KidsBirthdayCakes />} />
          <Route path="/birthday-cupcakes" element={<BirthdayCupcakes />} />

          <Route path="/anniversary-cakes" element={<AnniversaryCakes />} />
          <Route path="/couple-cakes" element={<CoupleCakes />} />
          <Route path="/anniversary-cupcakes" element={<AnniversaryCupcakes />} />

          <Route path="/photo-cakes" element={<PhotoCakes />} />
          <Route path="/kids-photo-cakes" element={<KidsPhotoCakes />} />
          <Route path="/photo-cupcakes" element={<PhotoCupcakes />} />

          <Route path="/flowers" element={<AllFlowers />} />
          <Route path="/valentine-flowers" element={<ValentineFlowers />} />
          <Route path="/birthday-flowers" element={<BirthdayFlowers />} />
          <Route path="/anniversary-flowers" element={<AnniversaryFlowers />} />

          <Route path="/designer-cakes" element={<DesignerCakes />} />
          <Route path="/theme-cakes" element={<ThemeCakes />} />
          <Route path="/kids-cakes" element={<KidsCakes />} />

          <Route path="/chocolate-cakes" element={<ChocolateCakes />} />
          <Route path="/butterscotch-cakes" element={<ButterscotchCakes />} />
          <Route path="/red-velvet-cakes" element={<RedVelvetCakes />} />
          <Route path="/pineapple-cakes" element={<PineappleCakes />} />
          <Route path="/black-forest-cakes" element={<BlackForestCakes />} />

          <Route path="/cakes-flower-combo" element={<CakeWithFlowers />} />
          <Route path="/cakes-teddy-combo" element={<CakeWithTeddy />} />
          <Route path="/cakes-chocolate-combo" element={<CakeWithChocolates />} />

          <Route path="/republic-day-cakes" element={<RepublicDayCakes />} />
          <Route path="/valentine-day-cakes" element={<ValentineDayCakes />} />
          <Route path="/rakhi" element={<Rakhi />} />
          <Route path="/bhai-dooj-cakes" element={<BhaiDoojCakes />} />
          <Route path="/dussehra-cakes" element={<DussehraCakes />} />
          <Route path="/diwali-cakes" element={<DiwaliCakes />} />
          <Route path="/holi-cakes" element={<HoliCakes />} />
          <Route path="/christmas-cakes" element={<ChristmasCakes />} />

          <Route path="/cakes-for-girlfriend" element={<CakesForGirlfriend />} />
          <Route path="/cakes-for-boyfriend" element={<CakesForBoyfriend />} />
          <Route path="/cakes-for-wife" element={<CakesForWife />} />
          <Route path="/cakes-for-husband" element={<CakesForHusband />} />
          <Route path="/cakes-for-sister" element={<CakesForSister />} />
          <Route path="/cakes-for-brother" element={<CakesForBrother />} />
          <Route path="/cakes-for-daughter" element={<CakesForDaughter />} />
          <Route path="/cakes-for-son" element={<CakesForSon />} />
          <Route path="/cakes-for-kids" element={<CakesForKids />} />
          <Route path="/cakes-for-friend" element={<CakesForFriend />} />
          <Route path="/cakes/womens-day-cakes" element={<WomensDayCakes />} />
          <Route path="/cakes-for-grand-mother" element={<CakesForGrandMother />} />
          <Route path="/cakes-for-grand-father" element={<CakesForGrandFather />} />
          <Route path="/cakes-for-mother" element={<CakesForMother />} />
          <Route path="/cakes-for-father" element={<CakesForFather />} />
          <Route path="/cakes/mothers-day-cakes" element={<MothersDayCakes />} />
          <Route path="/cakes/fathers-day-cakes" element={<FathersDayCakes />} />

          {/* Dynamic Location Routes for all cities */}
          <Route path="/cakes/:city" element={<LocationPage />} />
          <Route path="/cakes" element={<Cakes />} />
          <Route path="/sitemap" element={<Sitemap />} />
          
          <Route path="/cart" element={<Cart />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:id" element={<BlogDetail />} />
          <Route path="/personalized" element={<Personalized />} />
          <Route path="/wishes" element={<Wishes />} />
          <Route path="/in-news" element={<InNews />} />
          <Route path="/customize-cake" element={<CustomizePage />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          <Route path="/payments-and-security" element={<PaymentsSecurity />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/order-failed" element={<OrderFailed />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/login" element={<LoginSignup />} />
          <Route path="/admin/*" element={<AdminLayout />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      {!isAdminPage && location.pathname !== '/my-orders' && <SiloNavigation />}
      {!hideNav && <Footer />}
      {!hideNav && <BottomNav />}
    </div>
  )
}

export default Approutes;
